import React, { useState, useRef, useEffect, useCallback } from "react";
import { Card, Box, Typography, CardContent } from "@mui/material";
import { IconButton } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";

export default function ImageViewerCard({ image, isStructure, viewerId = "gioo-image-viewer" }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const dragStart = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const isVideo = (url) => {
    if (typeof url !== "string") return false;
    return url.match(/\.(mp4|webm|mkv|mov|avi)$/i);
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (!videoRef.current.paused) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!videoRef.current.paused);
    }
  };

  // ── Zoom ──
  const handleZoomIn = () => setScale((s) => Math.min(s + 0.25, 5));
  const handleZoomOut = () =>
    setScale((s) => {
      const next = Math.max(s - 0.25, 0.5);
      if (next <= 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  const handleReset = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // ── Mouse wheel zoom ──
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setScale((s) => {
      const next = Math.max(0.5, Math.min(s + delta, 5));
      if (next <= 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      el.addEventListener("wheel", handleWheel, { passive: false });
      return () => el.removeEventListener("wheel", handleWheel);
    }
  }, [handleWheel]);

  // ── Fullscreen toggle ──
  const handleFullscreen = () => {
    const el = document.getElementById(viewerId);
    if (!document.fullscreenElement) {
      el?.requestFullscreen?.() ?? el?.webkitRequestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  // ── Listen for browser fullscreen changes ──
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // ── Pan handlers (only active when zoomed in) ──
  const onMouseDown = (e) => {
    if (e.target.closest("button")) return;
    if (scale <= 1) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  };
  const onMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };
  const onMouseUp = () => setIsDragging(false);

  // ── Touch pan handlers for mobile ──
  const onTouchStart = (e) => {
    if (e.target.closest("button")) return;
    if (scale <= 1) return;
    const touch = e.touches[0];
    setIsDragging(true);
    dragStart.current = {
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    };
  };
  const onTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.current.x,
      y: touch.clientY - dragStart.current.y,
    });
  };
  const onTouchEnd = () => setIsDragging(false);

  // ── Top-right toolbar ──
  const toolbar = (
    <Box
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 0.2,
        bgcolor: "rgba(255,255,255,0.90)",
        borderRadius: "10px",
        px: 0.5,
        py: 0.3,
        boxShadow: "0 2px 10px rgba(0,0,0,0.14)",
        backdropFilter: "blur(6px)",
      }}
    >
      {/* Reset — only when zoomed */}
      {scale !== 1 && (
        <Tooltip title="Reset" arrow>
          <IconButton
            size="small"
            onClick={handleReset}
            sx={{
              color: "#4a8f3c",
              "&:hover": { bgcolor: "rgba(74,143,60,0.10)" },
            }}
          >
            <RestartAltIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Zoom In */}
      <Tooltip title="Zoom In" arrow>
        <IconButton
          size="small"
          onClick={handleZoomIn}
          sx={{ color: "#555", "&:hover": { color: "#4a8f3c", bgcolor: "transparent" } }}
        >
          <ZoomInIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      {/* Zoom Out */}
      <Tooltip title="Zoom Out" arrow>
        <IconButton
          size="small"
          onClick={handleZoomOut}
          sx={{ color: "#555", "&:hover": { color: "#4a8f3c", bgcolor: "transparent" } }}
        >
          <ZoomOutIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      {/* Fullscreen / Exit Fullscreen */}
      <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"} arrow>
        <IconButton
          size="small"
          onClick={handleFullscreen}
          sx={{ color: "#555", "&:hover": { color: "#4a8f3c", bgcolor: "transparent" } }}
        >
          {isFullscreen ? (
            <FullscreenExitIcon sx={{ fontSize: 20 }} />
          ) : (
            <FullscreenIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Card
      id={viewerId}
      sx={{
        borderRadius: 4,
        p: 2,
        boxShadow: 2,
        textAlign: "center",
        height: { xs: 680, sm: 520, md: "100%" },
        maxHeight: 400,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Title Banner */}
      <Box
        sx={{
          display: "inline-block",
          bgcolor: "#f1f8e9",
          borderRadius: 2,
          px: 2.5,
          py: 1,
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: "#4a8f3c" }}
        >
          GreenHeap Plots {isStructure ? "Structure" : "Layout Sketch"} Detail
        </Typography>
      </Box>

      {/* Image Area with toolbar overlaid top-right */}
      <CardContent
        ref={containerRef}
        sx={{
          flexGrow: { xs: 0, md: 1 },
          minHeight: { xs: 200, md: "auto" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          borderRadius: 2,
          border: "1px solid #ddd",
          p: 1,
          position: "relative",
          cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          userSelect: "none",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* TOP-RIGHT TOOLBAR */}
        {toolbar}

        {/* MEDIA */}
        {isVideo(image) ? (
          <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}
          >
            <Box
              component="video"
              ref={videoRef}
              src={image}
              loop
              muted
              playsInline
              disablePictureInPicture
              disableRemotePlayback
              controlsList="nopictureinpicture"
              onContextMenu={(e) => e.preventDefault()}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                transformOrigin: "center center",
                transition: isDragging ? "none" : "transform 0.22s ease",
              }}
            />
            {(!isPlaying || isHovered) && (
              <IconButton
                onClick={togglePlay}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "white",
                  borderRadius: "4px", // Rectangular
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                  zIndex: 10,
                }}
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            )}
          </Box>
        ) : (
          <Box
            component="img"
            src={image || "/assets/images/placeholder_img.png"}
            alt="GreenHeap Plot Layout"
            draggable={false}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              transformOrigin: "center center",
              transition: isDragging ? "none" : "transform 0.22s ease",
              pointerEvents: "none",
              display: "block",
              /* High-resolution zoom rendering */
              imageRendering: scale > 1 ? "high-quality" : "auto",
              willChange: "transform",
              backfaceVisibility: "hidden",
            }}
          />
        )}

        {/* Zoom % badge — bottom left */}
        {scale !== 1 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 8,
              left: 10,
              bgcolor: "rgba(0,0,0,0.42)",
              color: "#fff",
              borderRadius: "6px",
              px: 1,
              py: 0.2,
              fontSize: 11,
              fontWeight: 700,
              pointerEvents: "none",
            }}
          >
            {Math.round(scale * 100)}%
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
